import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  TrendingUp,
  Users,
  FileText,
  Search,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  Eye,
  Loader2,
  Calendar,
  ArrowUpRight,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config';

const InsightsPage = () => {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const [loading, setLoading] = useState(true);
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [orgAnalytics, setOrgAnalytics] = useState(null);
  const [spaceAnalytics, setSpaceAnalytics] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentOrg) {
      fetchSpaces();
      fetchOrgAnalytics();
    }
  }, [currentOrg, dateRange]);

  useEffect(() => {
    if (selectedSpace && selectedSpace !== 'all') {
      fetchSpaceAnalytics(selectedSpace);
    } else {
      setSpaceAnalytics(null);
    }
  }, [selectedSpace, dateRange]);

  const fetchSpaces = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizations/${currentOrg.id}/spaces`);
      setSpaces(response.data);
    } catch (error) {
      console.error('Failed to fetch spaces:', error);
    }
  };

  const fetchOrgAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/organizations/${currentOrg.id}/analytics?days=${dateRange}`);
      setOrgAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setError('load');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpaceAnalytics = async (spaceId) => {
    try {
      const response = await axios.get(`${API_URL}/spaces/${spaceId}/analytics?days=${dateRange}`);
      setSpaceAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch space analytics:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 lg:p-12 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error === 'load') {
    return (
      <div className="p-6 md:p-8 lg:p-12 animate-fade-in">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-16 h-16 mx-auto mb-6 text-slate-300 dark:text-slate-700" />
            <h2 className="text-2xl font-bold font-[Manrope] mb-4 text-slate-900 dark:text-white">
              Could not load analytics
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8 max-w-md mx-auto">
              Try again in a moment. If the problem persists, check that the API is running.
            </p>
            <Button onClick={() => fetchOrgAnalytics()} className="bg-purple-600 hover:bg-purple-700 text-white">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const analytics = selectedSpace === 'all' ? orgAnalytics : spaceAnalytics;

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">
            Insights
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Track how your documentation is performing
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedSpace} onValueChange={setSelectedSpace}>
            <SelectTrigger className="w-[180px]" data-testid="space-filter">
              <SelectValue placeholder="All Spaces" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Spaces</SelectItem>
              {spaces.map(space => (
                <SelectItem key={space.id} value={space.id}>{space.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<Eye className="w-5 h-5" />}
          label="Total Views"
          value={analytics?.total_views || 0}
          color="blue"
        />
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Unique Visitors"
          value={analytics?.unique_visitors || 0}
          color="green"
        />
        <StatCard
          icon={<Search className="w-5 h-5" />}
          label="Total Searches"
          value={orgAnalytics?.total_searches || 0}
          color="purple"
        />
        <StatCard
          icon={<ThumbsUp className="w-5 h-5" />}
          label="Helpful Rate"
          value={`${orgAnalytics?.feedback_summary?.helpful_percentage || 0}%`}
          color="amber"
        />
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="content">Top Content</TabsTrigger>
          <TabsTrigger value="search">Search Analytics</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Views Over Time Chart */}
          {spaceAnalytics?.views_over_time && spaceAnalytics.views_over_time.length > 0 && (
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Views Over Time
                </CardTitle>
                <CardDescription>Daily page views for {spaceAnalytics.space_name}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[200px] flex items-end gap-1">
                  {spaceAnalytics.views_over_time.map((day, i) => {
                    const maxViews = Math.max(...spaceAnalytics.views_over_time.map(d => d.views));
                    const height = maxViews > 0 ? (day.views / maxViews * 100) : 0;
                    return (
                      <div
                        key={i}
                        className="flex-1 bg-purple-500 dark:bg-purple-600 rounded-t transition-all hover:bg-purple-600 dark:hover:bg-purple-500"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${day.date}: ${day.views} views`}
                      />
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>{spaceAnalytics.views_over_time[0]?.date}</span>
                  <span>{spaceAnalytics.views_over_time[spaceAnalytics.views_over_time.length - 1]?.date}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-lg">Feedback Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-4xl font-bold text-slate-900 dark:text-white">
                    {orgAnalytics?.feedback_summary?.helpful_percentage || 0}%
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    of readers found<br />content helpful
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400 text-sm mb-1">
                      <ThumbsUp className="w-4 h-4" />
                      Helpful
                    </div>
                    <div className="text-2xl font-bold text-green-800 dark:text-green-300">
                      {orgAnalytics?.feedback_summary?.helpful_count || 0}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm mb-1">
                      <ThumbsDown className="w-4 h-4" />
                      Not Helpful
                    </div>
                    <div className="text-2xl font-bold text-red-800 dark:text-red-300">
                      {orgAnalytics?.feedback_summary?.not_helpful_count || 0}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-lg">Search Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Total Searches</span>
                    <span className="font-bold text-slate-900 dark:text-white">{orgAnalytics?.total_searches || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Failed Searches</span>
                    <span className="font-bold text-red-600">{orgAnalytics?.failed_searches || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Success Rate</span>
                    <span className="font-bold text-green-600">
                      {orgAnalytics?.total_searches > 0 
                        ? Math.round((1 - orgAnalytics.failed_searches / orgAnalytics.total_searches) * 100) 
                        : 0}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Top Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="font-[Manrope] flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Top Documents by Views
              </CardTitle>
              <CardDescription>Most viewed documents in the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {spaceAnalytics?.top_documents && spaceAnalytics.top_documents.length > 0 ? (
                <div className="space-y-3">
                  {spaceAnalytics.top_documents.map((doc, index) => (
                    <div 
                      key={doc.document_id}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-amber-100 text-amber-700' :
                        index === 1 ? 'bg-slate-200 text-slate-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">
                          {doc.document_title}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {doc.total_views} views
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {doc.unique_visitors} unique
                          </span>
                          {doc.total_feedback > 0 && (
                            <span className={`flex items-center gap-1 ${
                              doc.helpful_percentage >= 70 ? 'text-green-600' :
                              doc.helpful_percentage >= 40 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              <ThumbsUp className="w-3 h-3" />
                              {doc.helpful_percentage}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-slate-900 dark:text-white">
                          {doc.total_views}
                        </div>
                        <div className="text-xs text-slate-500">views</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-700" />
                  <p>Select a space to see top content</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Search Analytics Tab */}
        <TabsContent value="search" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Search Terms */}
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Top Search Terms
                </CardTitle>
                <CardDescription>What users are searching for</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {orgAnalytics?.search_terms && orgAnalytics.search_terms.length > 0 ? (
                    <div className="space-y-2">
                      {orgAnalytics.search_terms.map((term, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <span className="font-medium text-slate-900 dark:text-white">"{term.query}"</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{term.count}x</Badge>
                            <Badge className={term.success_rate >= 70 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {term.success_rate}% found
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Search className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No search data yet</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Missing Content */}
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 border-red-200 dark:border-red-900/50">
              <CardHeader>
                <CardTitle className="font-[Manrope] flex items-center gap-2 text-red-700 dark:text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  Missing Content
                </CardTitle>
                <CardDescription>Searches that returned no results - consider adding content for these topics</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {orgAnalytics?.missing_content && orgAnalytics.missing_content.length > 0 ? (
                    <div className="space-y-2">
                      {orgAnalytics.missing_content.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50">
                          <div>
                            <span className="font-medium text-slate-900 dark:text-white">"{item.query}"</span>
                            <p className="text-xs text-slate-500 mt-1">
                              Last searched: {new Date(item.last_searched).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant="destructive">{item.search_count}x</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-green-600">
                      <ThumbsUp className="w-12 h-12 mx-auto mb-4" />
                      <p className="font-medium">Great job!</p>
                      <p className="text-sm text-slate-500 mt-1">All searches are finding content</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="space-y-6">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="font-[Manrope] flex items-center gap-2">
                <HelpCircle className="w-5 h-5" />
                Feedback Overview
              </CardTitle>
              <CardDescription>How readers are rating your documentation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="text-center">
                  <div className={`text-6xl font-bold ${
                    (orgAnalytics?.feedback_summary?.helpful_percentage || 0) >= 70 
                      ? 'text-green-600' 
                      : (orgAnalytics?.feedback_summary?.helpful_percentage || 0) >= 40 
                        ? 'text-amber-600' 
                        : 'text-red-600'
                  }`}>
                    {orgAnalytics?.feedback_summary?.helpful_percentage || 0}%
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 mt-2">Overall Satisfaction</p>
                </div>
                <div className="flex-1 w-full max-w-md">
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-2 text-green-600">
                          <ThumbsUp className="w-4 h-4" />
                          Helpful
                        </span>
                        <span className="font-medium">{orgAnalytics?.feedback_summary?.helpful_count || 0}</span>
                      </div>
                      <Progress 
                        value={orgAnalytics?.feedback_summary?.helpful_percentage || 0}
                        className="h-3 [&>div]:bg-green-500"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-2 text-red-600">
                          <ThumbsDown className="w-4 h-4" />
                          Not Helpful
                        </span>
                        <span className="font-medium">{orgAnalytics?.feedback_summary?.not_helpful_count || 0}</span>
                      </div>
                      <Progress 
                        value={100 - (orgAnalytics?.feedback_summary?.helpful_percentage || 0)}
                        className="h-3 [&>div]:bg-red-500"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-4">
                    Total feedback: {orgAnalytics?.feedback_summary?.total_feedback || 0} responses
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  };

  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
          <div>
            <p className="text-sm text-slate-600 dark:text-slate-400">{label}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InsightsPage;
